import React, { useState } from 'react';
import axios from 'axios';
import { CreditCardIcon, GiftIcon, ChartBarIcon, UserPlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import QRCode from 'qrcode';
import OneSignalSetup from '../components/OneSignalSetup';
import NotificationPrompt from '../components/NotificationPrompt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FidelityPublic = () => {
  const [view, setView] = useState('landing'); // 'landing', 'register', 'search', 'card'
  const [customer, setCustomer] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [pointsHistory, setPointsHistory] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  
  // Registration form
  const [registerForm, setRegisterForm] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    whatsapp: '',
    birthday: ''
  });

  // Search form
  const [searchEmail, setSearchEmail] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [searchCode, setSearchCode] = useState('');
  const [searchMethod, setSearchMethod] = useState('email'); // 'email', 'phone', or 'code'

  const handleRegister = async (e) => {
    e.preventDefault();
    
    try {
      // Aggiungi prefisso +39 ai numeri se non presente
      const formData = {
        ...registerForm,
        phone: registerForm.phone.startsWith('+') ? registerForm.phone : `+39${registerForm.phone.replace(/\s/g, '')}`,
        whatsapp: registerForm.whatsapp 
          ? (registerForm.whatsapp.startsWith('+') ? registerForm.whatsapp : `+39${registerForm.whatsapp.replace(/\s/g, '')}`)
          : ''
      };
      
      const response = await axios.post(`${API}/fidelity/customers`, formData);
      setCustomer(response.data);
      await generateQRCode(response.data.qr_code_data);
      await loadPointsHistory(response.data.customer_code);
      await loadRewards();
      setView('card');
      setShowNotificationPrompt(true); // Mostra prompt notifiche
      toast.success('Vip Card creata con successo!');
    } catch (error) {
      console.error('Errore registrazione:', error);
      toast.error(error.response?.data?.detail || 'Errore nella registrazione');
    }
  };

  const handleSearchByEmail = async (e) => {
    e.preventDefault();
    
    if (!searchEmail) {
      toast.error('Inserisci la tua email');
      return;
    }

    try {
      const response = await axios.get(`${API}/fidelity/customers/by-email/${searchEmail}`);
      setCustomer(response.data);
      await generateQRCode(response.data.qr_code_data);
      await loadPointsHistory(response.data.customer_code);
      await loadRewards();
      setView('card');
      toast.success('Carta trovata!');
    } catch (error) {
      console.error('Errore ricerca:', error);
      toast.error('Nessuna carta trovata con questa email');
    }
  };


  const handleSearchByPhone = async (e) => {
    e.preventDefault();
    
    if (!searchPhone) {
      toast.error('Inserisci il tuo numero di telefono');
      return;
    }

    try {
      // Aggiungi prefisso +39 se non presente
      const phoneWithPrefix = searchPhone.startsWith('+') ? searchPhone : `+39${searchPhone.replace(/\s/g, '')}`;
      
      const response = await axios.get(`${API}/fidelity/customers/by-phone/${encodeURIComponent(phoneWithPrefix)}`);
      setCustomer(response.data);
      await generateQRCode(response.data.qr_code_data);
      await loadPointsHistory(response.data.customer_code);
      await loadRewards();
      setView('card');
      toast.success('Carta trovata!');
    } catch (error) {
      console.error('Errore ricerca:', error);
      toast.error('Nessuna carta trovata con questo numero di telefono');
    }
  };


  const handleSearchByCode = async (e) => {
    e.preventDefault();
    
    if (!searchCode) {
      toast.error('Inserisci il codice carta');
      return;
    }

    try {
      const response = await axios.get(`${API}/fidelity/customers/${searchCode}`);
      setCustomer(response.data);
      await generateQRCode(response.data.qr_code_data);
      await loadPointsHistory(response.data.customer_code);
      await loadRewards();
      setView('card');
      toast.success('Carta trovata!');
    } catch (error) {
      console.error('Errore ricerca:', error);
      toast.error('Nessuna carta trovata con questo codice');
    }
  };

  const generateQRCode = async (data) => {
    try {
      const url = await QRCode.toDataURL(data, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeUrl(url);
    } catch (error) {
      console.error('Errore generazione QR:', error);
    }
  };

  const loadPointsHistory = async (customerCode) => {
    try {
      const response = await axios.get(`${API}/fidelity/points/history/${customerCode}`);
      setPointsHistory(response.data);
    } catch (error) {
      console.error('Errore caricamento storico:', error);
    }
  };

  const loadRewards = async () => {
    try {
      const response = await axios.get(`${API}/fidelity/rewards?active_only=true`);
      setRewards(response.data);
    } catch (error) {
      console.error('Errore caricamento premi:', error);
    }
  };

  const handleRedeem = async (rewardId) => {
    if (!customer) return;

    if (!window.confirm('Confermi il riscatto di questo premio?')) {
      return;
    }

    try {
      await axios.post(`${API}/fidelity/rewards/redeem`, {
        customer_code: customer.customer_code,
        reward_id: rewardId
      });
      
      toast.success('Premio riscattato! Verifica nella tua email la conferma.');
      
      // Reload customer data
      const response = await axios.get(`${API}/fidelity/customers/${customer.customer_code}`);
      setCustomer(response.data);
      await loadPointsHistory(customer.customer_code);
    } catch (error) {
      console.error('Errore riscatto:', error);
      toast.error(error.response?.data?.detail || 'Errore nel riscatto del premio');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('it-IT');
  };

  const resetToLanding = () => {
    setView('landing');
    setCustomer(null);
    setQrCodeUrl('');
    setPointsHistory([]);
    setRewards([]);
    setSearchEmail('');
    setSearchCode('');
  };

  // LANDING PAGE
  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-block p-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl mb-4">
              <CreditCardIcon className="w-16 h-16 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Vip Card Lacinio
            </h1>
            <p className="text-xl text-gray-600">
              Accumula punti ad ogni acquisto e riscatta premi esclusivi
            </p>
          </div>

          {/* Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <Card className="hover:shadow-xl transition-shadow cursor-pointer" onClick={() => setView('register')}>
              <CardHeader>
                <UserPlusIcon className="w-12 h-12 text-blue-600 mb-4" />
                <CardTitle>Nuova Vip Card</CardTitle>
                <CardDescription>
                  Registrati e ottieni subito la tua Vip Card digitale
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button className="w-full">Registrati Ora</Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow cursor-pointer" onClick={() => setView('search')}>
              <CardHeader>
                <MagnifyingGlassIcon className="w-12 h-12 text-purple-600 mb-4" />
                <CardTitle>Ho già una Vip Card</CardTitle>
                <CardDescription>
                  Recupera la tua Vip Card con email, telefono o codice
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="outline" className="w-full">Accedi alla Vip Card</Button>
              </CardContent>
            </Card>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6 text-center">
                <ChartBarIcon className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold mb-2">1€ = 1 Punto</h3>
                <p className="text-sm text-gray-600">
                  Guadagna punti ad ogni acquisto nel nostro punto vendita
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <GiftIcon className="w-10 h-10 text-purple-600 mx-auto mb-3" />
                <h3 className="font-bold mb-2">Premi Esclusivi</h3>
                <p className="text-sm text-gray-600">
                  Riscatta i tuoi punti per ottenere prodotti e sconti
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <CreditCardIcon className="w-10 h-10 text-green-600 mx-auto mb-3" />
                <h3 className="font-bold mb-2">Vip Card Digitale</h3>
                <p className="text-sm text-gray-600">
                  Mostra il QR code al nostro staff per accumulare punti
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // REGISTRATION FORM
  if (view === 'register') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Button variant="outline" onClick={resetToLanding} className="mb-6">
            ← Indietro
          </Button>

          <Card>
            <CardHeader>
              <CardTitle>Registrazione Vip Card</CardTitle>
              <CardDescription>
                Compila i campi per ottenere la tua Vip Card digitale
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome *</Label>
                    <Input
                      id="name"
                      value={registerForm.name}
                      onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="surname">Cognome *</Label>
                    <Input
                      id="surname"
                      value={registerForm.surname}
                      onChange={(e) => setRegisterForm({...registerForm, surname: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Telefono *</Label>
                  <div className="flex gap-2">
                    <div className="flex items-center justify-center bg-gray-100 border border-gray-300 rounded-md px-3 text-gray-700 font-medium">
                      +39
                    </div>
                    <Input
                      id="phone"
                      type="tel"
                      value={registerForm.phone}
                      onChange={(e) => setRegisterForm({...registerForm, phone: e.target.value})}
                      placeholder="3XX XXX XXXX"
                      pattern="[0-9\s]*"
                      required
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="whatsapp">WhatsApp (opzionale)</Label>
                  <div className="flex gap-2">
                    <div className="flex items-center justify-center bg-gray-100 border border-gray-300 rounded-md px-3 text-gray-700 font-medium">
                      +39
                    </div>
                    <Input
                      id="whatsapp"
                      type="tel"
                      value={registerForm.whatsapp}
                      onChange={(e) => setRegisterForm({...registerForm, whatsapp: e.target.value})}
                      placeholder="3XX XXX XXXX"
                      pattern="[0-9\s]*"
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="birthday">Data di Nascita (opzionale)</Label>
                  <Input
                    id="birthday"
                    type="date"
                    value={registerForm.birthday}
                    onChange={(e) => setRegisterForm({...registerForm, birthday: e.target.value})}
                  />
                  <p className="text-xs text-gray-600">
                    💝 Riceverai uno sconto speciale per il tuo compleanno!
                  </p>
                </div>

                <div className="pt-4">
                  <Button type="submit" className="w-full" size="lg">
                    Crea la Mia Carta
                  </Button>
                </div>

                <p className="text-xs text-gray-600 text-center">
                  Registrandoti accetti di ricevere comunicazioni marketing via email e WhatsApp
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // SEARCH FORM
  if (view === 'search') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Button variant="outline" onClick={resetToLanding} className="mb-6">
            ← Indietro
          </Button>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Recupera con Email</CardTitle>
                <CardDescription>
                  Inserisci l'email usata durante la registrazione
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSearchByEmail} className="space-y-4">
                  <Input
                    type="email"
                    placeholder="tua@email.com"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                  />
                  <Button type="submit" className="w-full">
                    Cerca con Email
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recupera con Telefono</CardTitle>
                <CardDescription>
                  Inserisci il numero di telefono usato durante la registrazione
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSearchByPhone} className="space-y-4">
                  <div className="flex gap-2">
                    <div className="flex items-center justify-center bg-gray-100 border border-gray-300 rounded-md px-3 text-gray-700 font-medium">
                      +39
                    </div>
                    <Input
                      type="tel"
                      placeholder="3XX XXX XXXX"
                      value={searchPhone}
                      onChange={(e) => setSearchPhone(e.target.value)}
                      pattern="[0-9\s]*"
                      className="flex-1"
                    />
                  </div>
                  <Button type="submit" className="w-full">
                    Cerca con Telefono
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recupera con Codice Carta</CardTitle>
                <CardDescription>
                  Inserisci il codice numerico della tua carta
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSearchByCode} className="space-y-4">
                  <Input
                    type="text"
                    placeholder="1000000001"
                    value={searchCode}
                    onChange={(e) => setSearchCode(e.target.value)}
                    pattern="[0-9]*"
                  />
                  <Button type="submit" className="w-full">
                    Cerca con Codice
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // CARD VIEW
  if (view === 'card' && customer) {
    const redeemableRewards = rewards.filter(r => r.points_required <= customer.points_balance);
    
    return (
      <>
        {/* OneSignal Setup per associare utente */}
        <OneSignalSetup
          customerId={customer.customer_code}
          email={customer.email}
          name={`${customer.name} ${customer.surname}`}
        />
        
        {/* Notification Prompt */}
        {showNotificationPrompt && <NotificationPrompt />}
        
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <Button variant="outline" onClick={resetToLanding} className="mb-6">
              ← Esci
            </Button>

            {/* Customer Card */}
            <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white mb-6">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold mb-2">
                      {customer.name} {customer.surname}
                    </h2>
                    <p className="text-blue-100">Vip Card N° {customer.customer_code}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-blue-100 mb-1">Saldo Punti</p>
                    <p className="text-4xl font-bold">{customer.points_balance}</p>
                  </div>
              </div>

              {/* QR Code */}
              {qrCodeUrl && (
                <div className="bg-white p-4 rounded-lg inline-block">
                  <img src={qrCodeUrl} alt="QR Code" className="w-48 h-48" />
                  <p className="text-center text-xs text-gray-600 mt-2">
                    Mostra questo codice in cassa
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Redeemable Rewards */}
          {redeemableRewards.length > 0 && (
            <Card className="mb-6 border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="text-green-800">
                  🎉 Hai {redeemableRewards.length} premi disponibili!
                </CardTitle>
              </CardHeader>
            </Card>
          )}

          {/* Rewards Catalog */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Catalogo Premi</CardTitle>
              <CardDescription>Riscatta i tuoi punti</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {rewards.map((reward) => {
                  const canRedeem = reward.points_required <= customer.points_balance;
                  
                  return (
                    <Card key={reward.id} className={!canRedeem ? 'opacity-60' : 'border-green-200'}>
                      <CardContent className="pt-6">
                        {reward.image_url && (
                          <img
                            src={reward.image_url}
                            alt={reward.name}
                            className="w-full h-32 object-cover rounded-lg mb-3"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        )}
                        <h3 className="font-bold mb-2">{reward.name}</h3>
                        <p className="text-sm text-gray-600 mb-3">{reward.description}</p>
                        <div className="flex items-center justify-between">
                          <Badge variant={canRedeem ? "default" : "secondary"}>
                            {reward.points_required} punti
                          </Badge>
                          {canRedeem ? (
                            <Button
                              size="sm"
                              onClick={() => handleRedeem(reward.id)}
                            >
                              Riscatta
                            </Button>
                          ) : (
                            <span className="text-xs text-gray-500">
                              Ti mancano {reward.points_required - customer.points_balance} punti
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}

                {rewards.length === 0 && (
                  <p className="col-span-2 text-center text-gray-600 py-8">
                    Nessun premio disponibile al momento
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Points History */}
          <Card>
            <CardHeader>
              <CardTitle>Storico Punti</CardTitle>
            </CardHeader>
            <CardContent>
              {pointsHistory.length === 0 ? (
                <p className="text-center text-gray-600 py-8">
                  Nessun movimento ancora
                </p>
              ) : (
                <div className="space-y-2">
                  {pointsHistory.map((trans) => (
                    <div
                      key={trans.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-sm">{trans.description}</p>
                        <p className="text-xs text-gray-600">
                          {formatDateTime(trans.created_at)}
                        </p>
                      </div>
                      <div className={`text-lg font-bold ${trans.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {trans.amount > 0 ? '+' : ''}{trans.amount}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      </>
    );
  }

  return null;
};

export default FidelityPublic;
